/**
 * Single iterator for "every section in state" — shared by client-info-migrate,
 * link-manager, and the Find & Replace search/replace modules.
 *
 * Iteration order (visual reading order of the rendered site):
 *   1. sharedSections.navigation (once, even though it appears on every page)
 *   2. Every page's sections in page order, in section order
 *   3. sharedSections.footer (once)
 *
 * This is identical to the order link-manager.js used before consolidation
 * (shared nav → pages → shared footer), and the order Find & Replace presents
 * results in (so the user sees hits in the order they read the site).
 *
 * Shared-section dedupe is automatic: each shared section is visited exactly
 * once, so a single mutation propagates to every page that uses it without
 * the caller having to dedupe.
 *
 * NOTE: exporter.js was deliberately NOT migrated to this iterator in the
 * Find & Replace v1 work. Exporter has its own multi-pass traversal whose
 * exact ordering isn't pinned by tests; a silent regression there would be
 * hard to spot. Refactor exporter in a follow-up with a dedicated
 * section-order parity test.
 */

/**
 * @typedef {Object} SectionContext
 * @property {string} html           - The section's stored HTML
 * @property {string} sectionPath    - JSON-pointer-like path for store.update
 *                                     (e.g. "pages.0.sections.2.html" or
 *                                     "sharedSections.navigation.html")
 * @property {string|null} instanceId - The section's instanceId, if any
 * @property {'page-section'|'shared'} kind
 * @property {string} [pageId]       - For page-section only
 * @property {string} [pageName]     - For page-section only
 * @property {number} [pageIndex]    - For page-section only
 * @property {number} [sectionIndex] - For page-section only
 * @property {'navigation'|'footer'} [sharedKind] - For shared only
 * @property {Object} section        - The raw section object (avoid mutating)
 */

/**
 * Call `visitor(ctx)` for every section in state, in visual reading order.
 * Visitor receives a SectionContext; return value is ignored.
 */
export function iterateAllSections(state, visitor) {
  if (!state) return;

  // 1. Shared navigation (appears at top of every page)
  visitShared(state, 'navigation', visitor);

  // 2. Page sections
  if (Array.isArray(state.pages)) {
    state.pages.forEach((page, pageIndex) => {
      if (!page || !Array.isArray(page.sections)) return;
      page.sections.forEach((section, sectionIndex) => {
        if (!section || typeof section.html !== 'string') return;
        visitor({
          html: section.html,
          sectionPath: `pages.${pageIndex}.sections.${sectionIndex}.html`,
          instanceId: section.instanceId || null,
          kind: 'page-section',
          pageId: page.id,
          pageName: page.name || page.id || '',
          pageIndex,
          sectionIndex,
          section
        });
      });
    });
  }

  // 3. Shared footer (appears at bottom of every page)
  visitShared(state, 'footer', visitor);
}

function visitShared(state, sharedKind, visitor) {
  const shared = state.sharedSections && state.sharedSections[sharedKind];
  if (!shared || typeof shared.html !== 'string') return;
  visitor({
    html: shared.html,
    sectionPath: `sharedSections.${sharedKind}.html`,
    instanceId: shared.instanceId || null,
    kind: 'shared',
    sharedKind,
    section: shared
  });
}

/**
 * Convenience: iterate all sections and collect updates from a mapper.
 * Mapper receives a SectionContext and returns either a new html string
 * (different from ctx.html) or anything else (falsy / same string = no update).
 * Returns an array of { path, html, instanceId } update records — the same
 * shape callers feed to store.update + (optionally) preview surgical sync.
 */
export function mapAllSections(state, mapper) {
  const updates = [];
  iterateAllSections(state, (ctx) => {
    const result = mapper(ctx);
    if (typeof result === 'string' && result !== ctx.html) {
      updates.push({
        path: ctx.sectionPath,
        html: result,
        instanceId: ctx.instanceId
      });
    }
  });
  return updates;
}
